import { API_BASE_URL } from './api';

const CS_MKT_ID_KEY = 'checkstall_cs_mkt_id_v1';
const FIRST_TOUCH_KEY = 'checkstall_marketing_first_touch_v1';
const LATEST_TOUCH_KEY = 'checkstall_marketing_latest_touch_v1';
const SESSION_ID_KEY = 'checkstall_marketing_session_id_v1';

const MARKETING_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'fbclid',
  'gclid',
  'ttclid',
  'campaign_id',
  'adset_id',
  'ad_id',
  'creative_id',
  'meta_campaign_id',
  'meta_adset_id',
  'meta_ad_id',
];

const storage = {
  get(key) {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch {
      // Marketing storage must stay best-effort.
    }
  },
};

const sessionStorageSafe = {
  get(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch {
      // Fall through; session identity is best-effort.
    }
  },
};

const uuid = () => {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
};

export const getOrCreateCsMktId = () => {
  const existing = getStoredCsMktId();
  if (existing) {
    return existing;
  }
  const value = `csm_${uuid()}`;
  storage.set(CS_MKT_ID_KEY, value);
  return value;
};

export const getStoredCsMktId = () => {
  const existing = storage.get(CS_MKT_ID_KEY);
  const normalized = existing ? String(existing).trim() : '';
  return normalized || null;
};

export const getOrCreateMarketingSessionId = () => {
  const existing = sessionStorageSafe.get(SESSION_ID_KEY);
  if (existing) {
    return existing;
  }
  const value = `session_${uuid()}`;
  sessionStorageSafe.set(SESSION_ID_KEY, value);
  return value;
};

const getCurrentTouch = () => {
  const params = new URLSearchParams(window.location.search || '');
  const touch = {
    cs_mkt_id: getOrCreateCsMktId(),
    anonymous_id: getOrCreateCsMktId(),
    install_id: getOrCreateCsMktId(),
    session_id: getOrCreateMarketingSessionId(),
    platform: 'public_web',
    landing_url: window.location.href,
    referrer: document.referrer || '',
  };

  MARKETING_KEYS.forEach((key) => {
    const value = params.get(key);
    if (value) {
      touch[key] = value;
    }
  });

  if (touch.meta_campaign_id && !touch.campaign_id) {
    touch.campaign_id = touch.meta_campaign_id;
  }
  if (touch.meta_adset_id && !touch.adset_id) {
    touch.adset_id = touch.meta_adset_id;
  }
  if (touch.meta_ad_id && !touch.ad_id) {
    touch.ad_id = touch.meta_ad_id;
  }

  return touch;
};

const hasMarketingTouch = (touch = {}) => (
  MARKETING_KEYS.some((key) => Boolean(touch[key])) ||
  Boolean(touch.referrer && !touch.referrer.includes('checkstall.com'))
);

const buildEventPayload = (eventName, touch) => ({
  event_name: eventName,
  event_id: uuid(),
  properties: touch,
});

const sendAttributionPayload = async (eventName, touch, options = {}) => {
  const payload = buildEventPayload(eventName, touch);
  const url = `${API_BASE_URL}/app/marketing/attribution/events/`;

  if (options.preferBeacon && navigator.sendBeacon) {
    try {
      const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
      if (navigator.sendBeacon(url, blob)) {
        return null;
      }
    } catch {
      // Fall back to fetch below.
    }
  }

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Checkstall-Marketing-Id': touch.cs_mkt_id,
      'X-Marketing-Anonymous-Id': touch.cs_mkt_id,
      'X-Marketing-Install-Id': touch.cs_mkt_id,
      'X-Marketing-Session-Id': touch.session_id,
    },
    body: JSON.stringify(payload),
    keepalive: true,
  });
  return response.json().catch(() => null);
};

export const capturePublicAttribution = async (eventName = 'landing_page_view', options = {}) => {
  const touch = getCurrentTouch();
  if (!hasMarketingTouch(touch)) {
    return { cs_mkt_id: getOrCreateCsMktId(), captured: false };
  }

  const storedFirstTouch = storage.get(FIRST_TOUCH_KEY);
  if (!storedFirstTouch) {
    storage.set(FIRST_TOUCH_KEY, JSON.stringify({ ...touch, captured_at: new Date().toISOString() }));
  }
  storage.set(LATEST_TOUCH_KEY, JSON.stringify({ ...touch, captured_at: new Date().toISOString() }));

  try {
    const data = await sendAttributionPayload(eventName, touch, options);
    if (data?.cs_mkt_id) {
      storage.set(CS_MKT_ID_KEY, data.cs_mkt_id);
    }
  } catch {
    // Do not block navigation or downloads if tracking fails.
  }

  return { cs_mkt_id: getOrCreateCsMktId(), captured: true };
};

export const getMarketingHeaders = () => ({
  'X-Checkstall-Marketing-Id': getOrCreateCsMktId(),
  'X-Marketing-Anonymous-Id': getOrCreateCsMktId(),
  'X-Marketing-Install-Id': getOrCreateCsMktId(),
  'X-Marketing-Session-Id': getOrCreateMarketingSessionId(),
});

export const appendCsMktId = (url) => {
  try {
    const nextUrl = new URL(url, window.location.origin);
    nextUrl.searchParams.set('cs_mkt_id', getOrCreateCsMktId());
    return nextUrl.toString();
  } catch {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}cs_mkt_id=${encodeURIComponent(getOrCreateCsMktId())}`;
  }
};

export const appendExistingCsMktId = (url) => {
  const existing = getStoredCsMktId();
  if (!existing) {
    return url;
  }

  try {
    const nextUrl = new URL(url, window.location.origin);
    nextUrl.searchParams.set('cs_mkt_id', existing);
    return nextUrl.toString();
  } catch {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}cs_mkt_id=${encodeURIComponent(existing)}`;
  }
};

export const appendPlayStoreReferrer = (url) => {
  const referrer = new URLSearchParams({
    cs_mkt_id: getOrCreateCsMktId(),
  }).toString();
  try {
    const nextUrl = new URL(url);
    nextUrl.searchParams.set('referrer', referrer);
    return nextUrl.toString();
  } catch {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}referrer=${encodeURIComponent(referrer)}`;
  }
};
